import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Boxes,
  Columns3,
  List,
  ListChecks,
  Plus,
} from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Tabs,
  useToast,
  type SelectOption,
  type TabItem,
} from '../../ui';
import { useActiveRoom } from '../../features/rooms/RoomContext';
import { useTasks } from '../../features/tasks/useTasks';
import { useSprints } from '../../features/sprintboard/useSprints';
import {
  collectAssignees,
  collectTags,
  filterTasks,
  groupByStatus,
} from '../../features/tasks/taskModel';
import type { TaskFilter, TaskStatus, Task } from '../../features/tasks/types';
import { TaskBoard } from './TaskBoard';
import { TaskListView } from './TaskListView';
import { TaskModal } from './TaskModal';
import { QuickCreateTaskModal } from './QuickCreateTaskModal';
import { EstimateFlowModal } from './estimate/EstimateFlowModal';

type ViewMode = 'board' | 'list';

const VIEW_TABS: TabItem[] = [
  { id: 'board', label: 'Board', icon: <Columns3 className="h-4 w-4" /> },
  { id: 'list', label: 'List', icon: <List className="h-4 w-4" /> },
];

/**
 * Tasks page — a Kanban board (+ list view toggle) scoped to the active room.
 * Supports create/edit via a modal, drag-or-select status moves, soft-delete
 * with confirmation, and assignee/tag filtering. Reads exclusively through the
 * useTasks hook (which is itself scoped to the active room).
 */
export function TasksPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { activeRoom, activeRoomId } = useActiveRoom();
  const { tasks, loading, error, create, researchTask, update, move, remove, refresh } = useTasks();
  const { currentSprint } = useSprints();

  const [view, setView] = useState<ViewMode>('board');
  const [filter, setFilter] = useState<TaskFilter>({ assignee: '', tag: '' });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);
  const [createStatus, setCreateStatus] = useState<TaskStatus | undefined>();

  // Quick-create + post-create estimate flow.
  const [quickOpen, setQuickOpen] = useState(false);
  const [estimateTask, setEstimateTask] = useState<Task | null>(null);

  const [pendingDelete, setPendingDelete] = useState<Task | null>(null);
  const [deleting, setDeleting] = useState(false);

  const assigneeOptions = useMemo<SelectOption[]>(
    () => collectAssignees(tasks).map((a) => ({ value: a, label: a })),
    [tasks],
  );
  const tagOptions = useMemo<SelectOption[]>(
    () => collectTags(tasks).map((t) => ({ value: t, label: t })),
    [tasks],
  );

  const filtered = useMemo(() => filterTasks(tasks, filter), [tasks, filter]);
  const columns = useMemo(() => groupByStatus(filtered), [filtered]);
  const filtersActive = !!(filter.assignee || filter.tag);

  const openQuick = () => setQuickOpen(true);
  const openDetail = (task: Task) => navigate(`/tasks/${task.id}`);
  const openEdit = (task: Task) => {
    setEditing(task);
    setCreateStatus(undefined);
    setModalOpen(true);
  };

  // After quick-create: optionally open the estimate flow for the new task.
  const onCreated = (task: Task, estimateNow: boolean) => {
    if (estimateNow) setEstimateTask(task);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await remove(pendingDelete.id);
      toast({ tone: 'success', title: 'Task deleted', description: pendingDelete.title });
      setPendingDelete(null);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Delete failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const onMove = (task: Task, status: TaskStatus) => {
    void move(task, status).catch((e) =>
      toast({
        tone: 'error',
        title: 'Move failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  };

  // --- No active room: prompt selection ------------------------------------
  if (!activeRoomId) {
    return (
      <div>
        <PageHeader
          icon={<ListChecks className="h-5 w-5" />}
          title="Tasks"
          description="Track work items within a room."
        />
        <EmptyState
          icon={<Boxes className="h-6 w-6" />}
          title="No active room"
          description="Tasks live inside rooms. Select or create a room to start tracking work."
          action={
            <Button leftIcon={<Boxes className="h-4 w-4" />} onClick={() => navigate('/rooms')}>
              Go to Rooms
            </Button>
          }
        />
      </div>
    );
  }

  const headerActions = (
    <div className="flex items-center gap-2">
      <Tabs items={VIEW_TABS} value={view} onChange={(id) => setView(id as ViewMode)} />
      <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openQuick}>
        Create Task
      </Button>
    </div>
  );

  return (
    <div>
      <PageHeader
        icon={<ListChecks className="h-5 w-5" />}
        title="Tasks"
        description={
          <span className="inline-flex items-center gap-1.5">
            Tracking work in
            <Badge tone="accent">{activeRoom?.name ?? 'active room'}</Badge>
          </span>
        }
        actions={headerActions}
      />

      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Select
            label="Assignee"
            placeholder="All assignees"
            value={filter.assignee}
            options={assigneeOptions}
            onChange={(e) => setFilter((f) => ({ ...f, assignee: e.target.value }))}
          />
        </div>
        <div className="w-48">
          <Select
            label="Tag"
            placeholder="All tags"
            value={filter.tag}
            options={tagOptions}
            onChange={(e) => setFilter((f) => ({ ...f, tag: e.target.value }))}
          />
        </div>
        {filtersActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilter({ assignee: '', tag: '' })}
          >
            Clear filters
          </Button>
        )}
        <span className="ml-auto self-center text-xs text-zinc-500">
          {filtered.length} of {tasks.length}{' '}
          {tasks.length === 1 ? 'task' : 'tasks'}
        </span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading tasks" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load tasks"
          description={error}
        />
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={<ListChecks className="h-6 w-6" />}
          title="No tasks yet"
          description="Create the first task for this room to start tracking work."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openQuick}>
              Create Task
            </Button>
          }
        />
      ) : view === 'board' ? (
        <TaskBoard
          columns={columns}
          onMove={onMove}
          onEdit={openEdit}
          onOpen={openDetail}
          onDelete={setPendingDelete}
          onAdd={() => openQuick()}
        />
      ) : (
        <TaskListView tasks={filtered} onEdit={openEdit} onOpen={openDetail} />
      )}

      <TaskModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        task={editing}
        initialStatus={createStatus}
        onCreate={create}
        onSave={update}
      />

      <QuickCreateTaskModal
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        onCreate={create}
        onCreated={onCreated}
        onResearch={researchTask}
        currentSprint={currentSprint}
      />

      <EstimateFlowModal
        open={!!estimateTask}
        onClose={() => setEstimateTask(null)}
        task={estimateTask}
        onSaved={() => void refresh()}
      />

      <Modal
        open={!!pendingDelete}
        onClose={() => !deleting && setPendingDelete(null)}
        size="sm"
        title="Delete task"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setPendingDelete(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button variant="danger" loading={deleting} onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-400">
          Delete <span className="font-medium text-zinc-200">{pendingDelete?.title}</span>?
          This removes it from the board. This action can't be undone from the UI.
        </p>
      </Modal>
    </div>
  );
}

export default TasksPage;
